import { match } from 'ts-pattern';
import {
  debounceTime,
  filter,
  groupBy,
  map,
  merge,
  mergeMap,
  Observable,
  OperatorFunction,
} from 'rxjs';
import { Option } from '@sapphire/result';
import { Nullable } from '@ps/utils';

import {
  ADVANCED_FILTER_TYPE,
  AdvancedFeasibilityFilters,
  ComputeFeasibilityParams,
  ComputeFeasibilityParamsForPreDraft,
  SupplierAllocationsEditableContext,
  SurveyKind,
} from '@ps/survey-utils';

import {
  SurveyCreationCoreApiActions,
  SurveyCreationCoreChildrenApiActions,
} from '../../core/survey-creation-core.actions';
import {
  SurveyCreationAudienceActions,
  SurveyCreationChildrenActions,
  SurveyCreationChildrenApiActions,
} from '../../audience/survey-creation-audience.actions';
import { SurveyCreationInsightsActions } from '../survey-creation-insights.actions';
import { SurveyChildKey } from '../../survey-creation-shared-state.model';
import {
  SurveyChildRegistryState,
  SurveyCreationChildAudienceState,
} from '../../survey-creation-state.model';
import {
  DEFAULT_CHILD_AUDIENCE_STATE,
  DEFAULT_CORE_CHILD_STATE,
} from '../../survey-creation-state.constants';
import { resolveScopedChildKeys } from '../../survey-creation-child-registry.helper';
import { InsightsChildState } from '../survey-creation-insights-state.model';
import {
  KeyedRecalculationTrigger,
  RecalculationData,
  RecalculationTrigger,
} from './survey-creation-recalculation.types';

// Pending whenever Score/Feasibility is about to recalculate, or is blocked
// by manual processing (stays pending until Save or the toggle flips off).
export function shouldMarkAsPending(action: RecalculationTrigger): boolean {
  return match(action)
    .with(
      { type: SurveyCreationAudienceActions.toggleManualProcessing.type },
      (toggled) => !toggled.enabled,
    )
    .otherwise(() => true);
}

// The load's own recalculationsEnabled flag, threaded through from the init
// action that started it (see SurveyCreationCoreEffects). This is the opening
// surface's intent — the launch page loads a survey it does not want estimates
// for. Whether the *survey* may be recalculated at all is a separate question,
// answered by `selectSurveyAllowsRecalculation`.
//
// Traditional MC is the one exception: the flag is otherwise off on the
// Launch page, but its MC drawer still shows the active locale's CPI, so
// that child's initial feasibility load can't be suppressed there.
export function allowsRecalculation(action: RecalculationTrigger): boolean {
  return match(action)
    .with(
      {
        type: SurveyCreationCoreApiActions.loadActiveSurveyForEditSuccess.type,
      },
      (loaded) =>
        (loaded.recalculationsEnabled ?? true) ||
        SurveyKind.isTraditionalMc(loaded.survey),
    )
    .otherwise(() => true);
}

// Defines which actions should trigger a recalculation of Score and Feasibility.
export function qualifiesForRecalculation(
  action: RecalculationTrigger,
  enableManualProcessing: boolean,
): boolean {
  return match(action)
    .with(
      { type: SurveyCreationAudienceActions.toggleManualProcessing.type },
      (toggled) => !toggled.enabled,
    )
    .with(
      {
        type: SurveyCreationCoreApiActions.loadActiveSurveyForEditSuccess.type,
      },
      { type: SurveyCreationInsightsActions.refreshAfterSave.type },
      {
        type: SurveyCreationCoreChildrenApiActions.loadChildSurveysSuccess.type,
      },
      {
        type: SurveyCreationChildrenApiActions.updateChildSurveyCpiSuccess.type,
      },
      () => true,
    )
    .otherwise(() => !enableManualProcessing);
}

export function allowsRecalculationForMetadataPatch(
  action: RecalculationTrigger,
  isActiveMetadataValid: boolean,
): boolean {
  return (
    action.type !== SurveyCreationChildrenActions.patchChildMetadata.type ||
    isActiveMetadataValid
  );
}

// Load actions are one-shot data arrivals, not rapid user input — see
// `debounceEdits`.
export function isLoadAction(action: RecalculationTrigger): boolean {
  return (
    action.type ===
      SurveyCreationCoreApiActions.loadActiveSurveyForEditSuccess.type ||
    action.type ===
      SurveyCreationCoreChildrenApiActions.loadChildSurveysSuccess.type ||
    action.type ===
      SurveyCreationChildrenApiActions.updateChildSurveyCpiSuccess.type
  );
}

// Debounces edits only. Load actions pass straight through — debouncing them
// with edits lets one cancel the other when both fire off the same root
// success (loadChildSurveysSuccess + loadActiveSurveyForEditSuccess).
export function debounceEdits<T extends RecalculationTrigger>(
  ms: number,
): (source: Observable<T>) => Observable<T> {
  return (source) =>
    merge(
      source.pipe(filter((action) => isLoadAction(action))),
      source.pipe(
        filter((action) => !isLoadAction(action)),
        debounceTime(ms),
      ),
    );
}

export function debounceEditsPerKey(
  ms: number,
): OperatorFunction<KeyedRecalculationTrigger, SurveyChildKey> {
  return (source) =>
    source.pipe(
      groupBy(({ key }) => String(key)),
      mergeMap((trigger$) =>
        merge(
          trigger$.pipe(filter(({ action }) => isLoadAction(action))),
          trigger$.pipe(
            filter(({ action }) => !isLoadAction(action)),
            debounceTime(ms),
          ),
        ),
      ),
      map(({ key }) => key),
    );
}

// Which children a recalculation trigger targets, minus non-participating
// parents once a real family exists (see `resolveLoadedParticipant`).
export function resolveRecalculationKeys(
  action: RecalculationTrigger,
  activeChildKey: Nullable<SurveyChildKey>,
  coreChildren: Record<SurveyChildKey, SurveyChildRegistryState>,
): SurveyChildKey[] {
  const candidateKeys = match(action)
    .with(
      {
        type: SurveyCreationCoreChildrenApiActions.loadChildSurveysSuccess.type,
      },
      () => {
        const activeSurvey = coreChildren[activeChildKey ?? '']?.survey;
        const canDisagreeOnFirstLocale =
          !!activeSurvey && SurveyKind.isGroupMcOrTracker(activeSurvey);

        return canDisagreeOnFirstLocale
          ? Object.keys(coreChildren)
          : Object.keys(coreChildren).filter(
              (key) => Number(key) !== Number(activeChildKey),
            );
      },
    )
    .with(
      { type: SurveyCreationAudienceActions.updateMetadata.type },
      { type: SurveyCreationAudienceActions.updateQualifications.type },
      { type: SurveyCreationAudienceActions.updateQuotas.type },
      { type: SurveyCreationChildrenActions.patchChildMetadata.type },
      (scoped) =>
        resolveScopedChildKeys(coreChildren, activeChildKey, scoped.scope),
    )
    .with(
      {
        type: SurveyCreationChildrenApiActions.updateChildSurveyCpiSuccess.type,
      },
      ({ survey }) =>
        Number(survey.id) === Number(activeChildKey) ? [] : [survey.id],
    )
    .otherwise(() =>
      Option.from(activeChildKey)
        .map((key) => [key])
        .unwrapOr([]),
    );

  const isMultiLocale = Object.keys(coreChildren).length > 1;

  return isMultiLocale
    ? candidateKeys.filter((key) => coreChildren[key]?.participant !== false)
    : candidateKeys;
}

export interface RecalculationChildTask {
  key: SurveyChildKey;
  coreChild: SurveyChildRegistryState;
  audienceChild: SurveyCreationChildAudienceState;
  offerPrice: Nullable<number>;
}

// One task per resolved key, carrying each child's own slice of state —
// the unit of work the per-child feasibility pipeline runs on.
export function toRecalculationChildTasks(
  keys: SurveyChildKey[],
  coreChildren: Record<SurveyChildKey, SurveyChildRegistryState>,
  audienceChildren: Record<SurveyChildKey, SurveyCreationChildAudienceState>,
  insightsChildren: Record<SurveyChildKey, InsightsChildState>,
): RecalculationChildTask[] {
  return keys.map((key) => ({
    key,
    coreChild: coreChildren[key] ?? DEFAULT_CORE_CHILD_STATE,
    audienceChild: audienceChildren[key] ?? DEFAULT_CHILD_AUDIENCE_STATE,
    offerPrice: insightsChildren[key]?.pricing.offerPrice ?? null,
  }));
}

export function toSupplierContext(
  data: RecalculationData,
): SupplierAllocationsEditableContext {
  return {
    loi: data.metadata.loi ?? 0,
    ir: data.metadata.incidence ?? 0,
    languageCode: data.survey.mapOr('', (survey) => survey.languageCode ?? ''),
    fieldTime: data.metadata.fieldTime ?? 0,
    goal: data.metadata.completes ?? 0,
    clickBalancing: data.metadata.clickBalancing,
  };
}

// Params for the feasibility request. Grouping only applies to an Exclude
// survey with exclusions enabled and at least one group — otherwise
// `isExclusion` still follows the committed filter type, just ungrouped.
export function computeFeasibilityParams(
  data: RecalculationData,
  advancedFilters: AdvancedFeasibilityFilters,
  supplierIds: number[],
  offerPrice: Option<number>,
): ComputeFeasibilityParams {
  const isExclusion = advancedFilters.type === ADVANCED_FILTER_TYPE.EXCLUDE;

  // PD-17726: `isExclusion` always follows the filter type; an ungrouped
  // request zeroes the grouping instead, not the type.
  const base: ComputeFeasibilityParams = {
    surveyId: data.surveyId,
    user: data.user,
    metadata: data.metadata,
    qualifications: data.qualifications,
    quotas: data.quotas,
    catalogQualifications: data.catalogQualifications,
    offerPrice,
    supplierIds,
    surveyGroupIds: [],
    isExclusion,
    groupingStatuses: [],
  };

  if (!isExclusion || !advancedFilters.excludeFromFeasibleAudience) {
    return base;
  }

  const allGroupIds = [
    ...new Set([
      ...advancedFilters.matchedGroupIds,
      ...advancedFilters.manualGroupIds,
    ]),
  ];

  if (!allGroupIds.length) {
    return base;
  }

  return {
    ...base,
    surveyGroupIds: allGroupIds,
    groupingStatuses: [...advancedFilters.respondentStatuses],
  };
}

export function computeFeasibilityParamsForPreDraft(
  user: ComputeFeasibilityParamsForPreDraft['user'],
  formData: Pick<
    ComputeFeasibilityParamsForPreDraft,
    'metadata' | 'qualifications' | 'quotas'
  >,
): ComputeFeasibilityParamsForPreDraft {
  return { user, ...formData };
}
